import { useEffect, useRef, type ReactElement, type RefObject } from 'react';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import styled from 'styled-components';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import { colors, zIndex } from '@ardoq/design-tokens';
import { Box, Stack } from '@ardoq/layout';
import { Header3, Text } from '@ardoq/typography';
import { GhostButton } from '@ardoq/button';
import { ArdoqLoaderComponent, LoaderColor, LoaderSize } from '@ardoq/loader';
import { ProgressBar } from '@ardoq/decorators';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { PRESENTATION_CHROME_CLASS } from 'presentation/chrome';
import { pptxExport$, pptxExportCancelRequested } from './pptxExport$';
import {
  toExportOverlayProps,
  type ExportOverlayProps,
} from './exportOverlayProps';

const HEADING_ID = 'pptx-export-overlay-heading';
const PROGRESS_BAR_WIDTH = 360;
const ACTIVATION_KEYS = new Set(['Enter', ' ']);

const OverlayLayer = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.MODAL_BACKDROP};
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${colors.bgSubtle};
  outline: none;
`;

type PptxExportOverlayProps = { overlay: ExportOverlayProps | null };

const cancelExport = (): void => {
  dispatchAction(pptxExportCancelRequested());
};

const preventUnload = (event: BeforeUnloadEvent): void => {
  event.preventDefault();
};

const useBlockViewerInput = (
  cancelRef: RefObject<HTMLButtonElement | null>,
  canCancel: boolean
): void => {
  useEffect(() => {
    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    cancelRef.current?.focus();

    const blockKey = (event: KeyboardEvent): void => {
      event.stopImmediatePropagation();
      if (event.key === 'Escape' && canCancel) {
        cancelExport();
      }
      const activatesCancel =
        event.target === cancelRef.current && ACTIVATION_KEYS.has(event.key);
      if (!activatesCancel) {
        event.preventDefault();
      }
    };

    window.addEventListener('keydown', blockKey, true);
    window.addEventListener('beforeunload', preventUnload);
    return () => {
      window.removeEventListener('keydown', blockKey, true);
      window.removeEventListener('beforeunload', preventUnload);
      previouslyFocused?.focus();
    };
  }, [cancelRef, canCancel]);
};

const ExportOverlay = ({
  heading,
  slideTitle,
  loaderAriaLabel,
  progress,
  cancelLabel,
  canCancel,
}: ExportOverlayProps): ReactElement => {
  const cancelRef = useRef<HTMLButtonElement>(null);
  useBlockViewerInput(cancelRef, canCancel);

  return (
    <OverlayLayer
      className={PRESENTATION_CHROME_CLASS}
      role="dialog"
      aria-modal="true"
      aria-labelledby={HEADING_ID}
      tabIndex={-1}
      data-test-id="presentation-export-pptx-overlay"
    >
      <Stack gap="large" align="center">
        <ArdoqLoaderComponent
          size={LoaderSize.MEDIUM}
          loaderColor={LoaderColor.BRAND}
          loaderType="spinner"
          loaderText={null}
          ariaLabel={loaderAriaLabel}
        />
        <Box maxWidth={`${PROGRESS_BAR_WIDTH}px`}>
          <Stack gap="small" align="center" aria-live="polite">
            <Header3 id={HEADING_ID} align="center">
              {heading}
            </Header3>
            {slideTitle && (
              <Text variant="text1Bold" align="center">
                {slideTitle}
              </Text>
            )}
          </Stack>
        </Box>
        <ProgressBar
          percentValue={progress.percentValue}
          width={PROGRESS_BAR_WIDTH}
          progressbar={{
            label: progress.label,
            valueNow: progress.valueNow,
            valueMax: progress.valueMax,
            valueText: progress.valueText,
          }}
        />
        <GhostButton
          ref={cancelRef}
          onClick={cancelExport}
          isDisabled={!canCancel}
          dataTestId="presentation-export-pptx-cancel"
        >
          {cancelLabel}
        </GhostButton>
      </Stack>
    </OverlayLayer>
  );
};

const PptxExportOverlayContent = ({
  overlay,
}: PptxExportOverlayProps): ReactElement | null => {
  if (!overlay) {
    return null;
  }
  return <ExportOverlay {...overlay} />;
};

export const PptxExportOverlay = connect(
  PptxExportOverlayContent,
  combineLatest([pptxExport$, i18nLocale$]).pipe(
    map(([state, i18nLocale]) => ({
      overlay: toExportOverlayProps(state, i18nLocale),
    }))
  )
);
